import React from 'react'
import { Link } from 'react-router-dom'
import { 
  HiArrowLeft, 
  HiLightBulb, 
  HiServer, 
  HiChartBar, 
  HiCursorArrowRays, 
  HiBolt, 
  HiUsers, 
  HiArrowTrendingUp, 
  HiSignal,
  HiCheckCircle,
  HiExclamationTriangle,
  HiXCircle,
  HiCpuChip,
  HiGlobeAlt,
  HiChatBubbleLeftRight,
  HiFunnel,
  HiCalculator,
  HiTrophy,
  HiClock,
  HiArrowPath
} from 'react-icons/hi2'

const HowItWorksPage: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center space-x-2">
          <Link 
            to="/" 
            className="flex items-center space-x-2 text-gray-300 hover:text-orange-400 transition-colors"
          >
            <HiArrowLeft className="h-4 w-4" />
            <span>Back to Games</span>
          </Link>
        </div>
      </div>

      {/* Main Header */}
      <div className="text-center">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
          How It Works
        </h1>
        <p className="text-base sm:text-lg text-gray-300 mb-6 sm:mb-8">
          Understanding our AI-powered football sentiment analysis system
        </p>
      </div>

      {/* Overview Section */}
      <div className="card">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center">
          <HiLightBulb className="h-5 w-5 sm:h-6 sm:w-6 mr-3 text-orange-600" />
          System Overview
        </h2>
        <div className="prose max-w-none">
          <p className="text-base sm:text-lg text-gray-300 mb-6">
            Our Football Sentiment Tracker uses advanced AI to analyze fan sentiment from Reddit comments, 
            providing insights and predictions for EPL football matches. The system combines multiple 
            sophisticated techniques to ensure accurate and unbiased analysis.
          </p>
          
          <div className="bg-gray-800 p-4 sm:p-6 rounded-xl border border-gray-700 mb-6">
            <h3 className="text-base sm:text-lg font-semibold text-orange-400 mb-3">Why Reddit?</h3>
            <p className="text-sm sm:text-base text-gray-300 mb-3">
              Reddit provides a unique platform for authentic fan sentiment analysis. Unlike traditional 
              social media, Reddit's team subreddits create dedicated communities where fans engage in 
              detailed discussions about their teams. These communities offer:
            </p>
            <ul className="list-disc list-inside text-sm sm:text-base text-gray-300 space-y-2 mb-3">
              <li><strong>Authentic Fan Voices:</strong> Team subreddits are moderated communities where genuine fans discuss matches, tactics, and team news</li>
              <li><strong>Pre-Match Discussion:</strong> Fans actively discuss upcoming matches in pre-match threads, providing valuable sentiment data before kickoff</li>
              <li><strong>Engagement Metrics:</strong> Reddit's upvote/downvote system helps identify high-quality, relevant comments that resonate with the community</li>
              <li><strong>Rich Context:</strong> Longer comment threads allow for nuanced discussions that capture sentiment better than short-form social media posts</li>
            </ul>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-8">
            <div className="text-center p-5 sm:p-6 bg-gray-800 rounded-xl border border-gray-700">
              <HiServer className="h-10 w-10 sm:h-12 sm:w-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Data Collection</h3>
              <p className="text-sm sm:text-base text-gray-300">Extraction of Reddit comments from team subreddits before the match starts</p>
            </div>
            
            <div className="text-center p-5 sm:p-6 bg-gray-800 rounded-xl border border-gray-700">
              <HiCpuChip className="h-10 w-10 sm:h-12 sm:w-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">AI Analysis</h3>
              <p className="text-sm sm:text-base text-gray-300">Uses XLM-RoBERTa model with football-specific enhancements for sentiment analysis</p>
            </div>
            
            <div className="text-center p-5 sm:p-6 bg-gray-800 rounded-xl border border-gray-700">
              <HiCursorArrowRays className="h-10 w-10 sm:h-12 sm:w-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Bias Correction</h3>
              <p className="text-sm sm:text-base text-gray-300">Applies club-specific bias normalization for fair analysis</p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Collection Process */}
      <div className="card">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center">
          <HiGlobeAlt className="h-5 w-5 sm:h-6 sm:w-6 mr-3 text-orange-600" />
          Data Collection Process
        </h2>
        
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-4 space-y-3 sm:space-y-0">
            <div className="flex-shrink-0 w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">1</span>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Reddit Comments Extraction</h3>
              <p className="text-sm sm:text-base text-gray-300 mb-3">
                Our system automatically extracts comments from team-specific subreddits (like r/MCFC, r/Gunners) 
                before the match starts.
              </p>
              <div className="bg-gray-800 p-4 sm:p-5 rounded-lg border border-gray-700 mb-3">
                <h4 className="font-medium text-orange-400 mb-2">Why Team Subreddits?</h4>
                <p className="text-xs sm:text-sm text-gray-300 mb-2">
                  Team-specific subreddits provide isolated fan communities where supporters discuss their team 
                  with less interference from opposing fans. This allows us to:
                </p>
                <ul className="text-xs sm:text-sm text-gray-300 space-y-1 mb-2">
                  <li>• Capture authentic, unfiltered fan sentiment for each team</li>
                  <li>• Classify comments as home fan, away fan, or neutral based on subreddit membership</li>
                  <li>• Access pre-match threads where fans express predictions and expectations</li>
                  <li>• Gather general discussion threads for bias normalization (non-match sentiment)</li>
                </ul>
              </div>
              <div className="bg-gray-800 p-4 sm:p-5 rounded-lg border border-gray-700">
                <h4 className="font-medium text-orange-400 mb-2">Data Sources:</h4>
                <ul className="text-xs sm:text-sm text-gray-300 space-y-1">
                  <li>• Team subreddits for fan-specific sentiment</li>
                  <li>• Pre-match comments for sentiment analysis</li>
                  <li>• Non-match threads and discussion posts for bias calculation</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-4 space-y-3 sm:space-y-0">
            <div className="flex-shrink-0 w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">2</span>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Comment Filtering</h3>
              <p className="text-sm sm:text-base text-gray-300 mb-3">
                Comments are filtered for relevance and quality to ensure only meaningful football-related 
                content is analyzed.
              </p>
              <div className="bg-gray-800 p-4 sm:p-5 rounded-lg border border-gray-700">
                <h4 className="font-medium text-orange-400 mb-2">Filtering Criteria:</h4>
                <ul className="text-xs sm:text-sm text-gray-300 space-y-1">
                  <li>• Football context detection (football_relevance scoring)</li>
                  <li>• Minimum length requirements</li>
                  <li>• Spam and bot detection</li>
                  <li>• Relevance scoring (quality + engagement + football relevance)</li>
                </ul>
                <p className="text-[11px] sm:text-xs text-gray-400 mt-2">
                  Comments are scored using a weighted combination of quality metrics, engagement scores, 
                  and football relevance to ensure only meaningful content is analyzed.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-4 space-y-3 sm:space-y-0">
            <div className="flex-shrink-0 w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">3</span>
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-white mb-2">Fan Classification</h3>
              <p className="text-sm sm:text-base text-gray-300 mb-3">
                Comments are automatically classified as coming from home fans, away fans, or neutral observers 
                based on subreddit membership and comment context.
              </p>
              <div className="bg-gray-800 p-4 sm:p-5 rounded-lg border border-gray-700">
                <h4 className="font-medium text-orange-400 mb-2">Classification Methods:</h4>
                <ul className="text-xs sm:text-sm text-gray-300 space-y-1">
                  <li>• Subreddit-based classification</li>
                  <li>• Team mention analysis</li>
                  <li>• Sentiment pattern recognition</li>
                  <li>• Contextual clues detection</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* AI Sentiment Analysis */}
      <div className="card">
        <h2 className="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center">
          <HiCpuChip className="h-5 w-5 sm:h-6 sm:w-6 mr-3 text-orange-600" />
          AI Sentiment Analysis
        </h2>
        
        <div className="space-y-6">
          <div className="bg-gray-800 p-4 sm:p-6 rounded-xl border border-gray-700">
            <h3 className="text-base sm:text-lg font-semibold text-orange-400 mb-4">Enhanced XLM-RoBERTa Model</h3>
            <p className="text-sm sm:text-base text-gray-300 mb-4">
              We use a enhanced XLM-RoBERTa model, a state-of-the-art language model that understands football sentiment 
              analysis. This model understands context, sarcasm, and football-specific terminology.
            </p>
            
            <div className="bg-black p-4 rounded-lg border border-gray-700 mb-4">
              <h4 className="font-medium text-orange-400 mb-2">Why XLM-RoBERTa?</h4>
              <p className="text-xs sm:text-sm text-gray-300 mb-2">
                XLM-RoBERTa (Robustly Optimized BERT Pretraining Approach) was chosen for several key advantages:
              </p>
              <ul className="text-xs sm:text-sm text-gray-300 space-y-1">
                <li>• <strong>Advanced Context Understanding:</strong> Better at understanding nuanced language and context than simpler models</li>
                <li>• <strong>Sarcasm Detection:</strong> Recognizes irony and sarcasm common in football fan discussions</li>
                <li>• <strong>Multilingual Support:</strong> Handles English and other languages used in football communities</li>
                <li>• <strong>Proven Performance:</strong> State-of-the-art results in sentiment analysis tasks</li>
              </ul>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-black p-4 rounded-lg border border-gray-700">
                <h4 className="font-medium text-orange-400 mb-2">Model Capabilities:</h4>
                <ul className="text-xs sm:text-sm text-gray-300 space-y-1">
                  <li>• Multilingual support (English, etc.)</li>
                  <li>• Context-aware sentiment analysis</li>
                  <li>• Sarcasm and irony detection</li>
                  <li>• Football terminology understanding</li>
                </ul>
              </div>
              <div className="bg-black p-4 rounded-lg border border-gray-700">
                <h4 className="font-medium text-orange-400 mb-2">Football Enhancements:</h4>
                <ul className="text-xs sm:text-sm text-gray-300 space-y-1">
                  <li>• Custom football vocabulary and terminology</li>
                  <li>• Team-specific context processing</li>
                  <li>• Match situation awareness</li>
                  <li>• Fan language patterns and slang recognition</li>
                  <li>• Relevance scoring to prioritize football-related content</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 p-4 sm:p-6 rounded-xl border border-gray-700">
            <h3 className="text-base sm:text-lg font-semibold text-orange-400 mb-4">Loughran-McDonald Dictionary Integration</h3>
            <p className="text-sm sm:text-base text-gray-300 mb-4">
              To enhance the accuracy of our sentiment analysis, we combine the XLM-RoBERTa model with the 
              Loughran-McDonald Master Dictionary. This dictionary-based approach complements the AI model 
              by providing additional sentiment signals and detecting uncertainty markers in language.
            </p>
            
            <div className="bg-black p-4 rounded-lg border border-gray-700 mb-4">
              <h4 className="font-medium text-orange-400 mb-2">What is the Loughran-McDonald Dictionary?</h4>
              <p className="text-xs sm:text-sm text-gray-300 mb-2">
                The Loughran-McDonald Master Dictionary is a comprehensive financial sentiment dictionary that 
                categorizes words into sentiment and linguistic categories. Originally developed for financial 
                text analysis, it's highly effective for detecting nuanced sentiment patterns and hedging language.
              </p>
              <ul className="text-xs sm:text-sm text-gray-300 space-y-1">
                <li>• <strong>Positive & Negative Words:</strong> Categorizes words by sentiment polarity</li>
                <li>• <strong>Uncertainty Markers:</strong> Identifies hedging language (e.g., "might", "possibly", "uncertain")</li>
                <li>• <strong>Constraining Words:</strong> Detects limiting language that reduces confidence</li>
                <li>• <strong>Modal Words:</strong> Recognizes strong and weak modal verbs that indicate certainty levels</li>
              </ul>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-black p-4 rounded-lg border border-gray-700">
                <h4 className="font-medium text-orange-400 mb-2">How It Enhances Analysis:</h4>
                <ul className="text-xs sm:text-sm text-gray-300 space-y-1">
                  <li>• <strong>Hybrid Scoring:</strong> Combines AI model with dictionary analysis for more accurate sentiment</li>
                  <li>• <strong>Uncertainty Detection:</strong> Identifies hedging language that may reduce prediction confidence</li>
                  <li>• <strong>Confidence Adjustment:</strong> Adjusts confidence scores based on uncertainty and constraining word ratios</li>
                  <li>• <strong>Complementary Signals:</strong> Provides additional sentiment signals that complement the AI model's context understanding</li>
                </ul>
              </div>
              <div className="bg-black p-4 rounded-lg border border-gray-700">
                <h4 className="font-medium text-orange-400 mb-2">Example Usage:</h4>
                <p className="text-xs sm:text-sm text-gray-300 mb-2">
                  When analyzing a comment like "We might win if we play well", the dictionary detects:
                </p>
                <ul className="text-xs sm:text-sm text-gray-300 space-y-1">
                  <li>• "might" → Uncertainty marker (reduces confidence)</li>
                  <li>• "win" → Positive sentiment</li>
                  <li>• "well" → Positive sentiment</li>
                  <li>• Result: Positive sentiment but with lower confidence due to uncertainty</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-base sm:text-lg font-semibold text-white">Sentiment Processing Pipeline</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-800 rounded-lg border border-gray-700">
                <HiChatBubbleLeftRight className="h-8 w-8 text-orange-400 mx-auto mb-2" />
                <h4 className="font-medium text-white mb-1">Text Cleaning</h4>
                <p className="text-xs sm:text-sm text-gray-300">Remove noise, normalize text</p>
              </div>
              
              <div className="text-center p-4 bg-gray-800 rounded-lg border border-gray-700">
                <HiFunnel className="h-8 w-8 text-orange-400 mx-auto mb-2" />
                <h4 className="font-medium text-white mb-1">Context Detection</h4>
                <p className="text-xs sm:text-sm text-gray-300">Identify football context</p>
              </div>
              
              <div className="text-center p-4 bg-gray-800 rounded-lg border border-gray-700">
                <HiLightBulb className="h-8 w-8 text-orange-400 mx-auto mb-2" />
                <h4 className="font-medium text-white mb-1">AI Analysis</h4>
                <p className="text-xs sm:text-sm text-gray-300">XLM-RoBERTa processing</p>
              </div>
              
              <div className="text-center p-4 bg-gray-800 rounded-lg border border-gray-700">
                <HiCalculator className="h-8 w-8 text-orange-400 mx-auto mb-2" />
                <h4 className="font-medium text-white mb-1">Score Calculation</h4>
                <p className="text-xs sm:text-sm text-gray-300">Generate sentiment scores</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bias Normalization */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
          <HiCursorArrowRays className="h-6 w-6 mr-3 text-orange-600" />
          Bias Normalization System
        </h2>
        
        <div className="space-y-6">
          <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
            <h3 className="text-lg font-semibold text-orange-400 mb-4">Why Bias Normalization?</h3>
            <p className="text-gray-300 mb-4">
              Different fanbases have different communication styles and sentiment patterns. Some teams' fans 
              are naturally more optimistic, while others tend to be more critical. Our bias normalization 
              system ensures fair analysis by accounting for these baseline differences.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">7-Day Bias Calculation</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <HiClock className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Data Collection Period</h4>
                    <p className="text-sm text-gray-300">
                      We analyze 7 days of non-match comments prior to the match from each team's subreddit to establish 
                      their baseline sentiment patterns.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <HiFunnel className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Non-Match Filtering</h4>
                    <p className="text-sm text-gray-300">
                      Only general discussion comments are used, excluding match-related posts to get 
                      unbiased baseline sentiment.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <HiCalculator className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Bias Calculation</h4>
                    <p className="text-sm text-gray-300">
                      The average sentiment score from non-match comments becomes the team's bias offset, 
                      which is subtracted from match comments.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <HiArrowPath className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Regular Updates</h4>
                    <p className="text-sm text-gray-300">
                      Bias values are recalculated in each analysis to account for changing fan sentiment patterns.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Example: Bias Normalization</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-black rounded-lg border border-gray-700">
                  <h4 className="font-medium text-white mb-2">Raw Sentiment</h4>
                  <div className="text-2xl font-bold text-orange-400">+0.3</div>
                  <p className="text-sm text-gray-300">Before normalization</p>
                </div>
                
                <div className="text-center p-4 bg-black rounded-lg border border-gray-700">
                  <h4 className="font-medium text-white mb-2">Team Bias</h4>
                  <div className="text-2xl font-bold text-orange-400">+0.1</div>
                  <p className="text-sm text-gray-300">7-day baseline</p>
                </div>
                
                <div className="text-center p-4 bg-black rounded-lg border border-gray-700">
                  <h4 className="font-medium text-white mb-2">Normalized</h4>
                  <div className="text-2xl font-bold text-orange-400">+0.2</div>
                  <p className="text-sm text-gray-300">Fair comparison</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 text-center">
                Formula: Normalized Score = Raw Sentiment - Team Bias
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Prediction Engine */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
          <HiTrophy className="h-6 w-6 mr-3 text-orange-600" />
          Prediction Engine
        </h2>
        
        <div className="space-y-6">
          <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
            <h3 className="text-lg font-semibold text-orange-400 mb-4">How Predictions Work</h3>
            <p className="text-gray-300 mb-4">
              Our prediction engine combines normalized sentiment scores with confidence metrics to predict 
              match outcomes. The system considers fan sentiment, engagement levels, relevance scores, and 
              historical patterns. Each comment's contribution to the prediction is weighted by its 
              relevance_score, ensuring high-quality, football-relevant comments have more impact.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Input Factors</h3>
              
              <div className="space-y-3">
                
                <div className="flex items-center space-x-3">
                  <HiUsers className="h-5 w-5 text-orange-400" />
                  <div>
                    <h4 className="font-medium text-white">Relevance-Weighted Scores</h4>
                    <p className="text-sm text-gray-300">Each comment's sentiment is weighted by its relevance_score (quality + engagement + football relevance)</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <HiSignal className="h-5 w-5 text-orange-400" />
                  <div>
                    <h4 className="font-medium text-white">Data Quality & Sample Size</h4>
                    <p className="text-sm text-gray-300">Effective sample size (sum of relevance scores) and data freshness</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <HiCursorArrowRays className="h-5 w-5 text-orange-400" />
                  <div>
                    <h4 className="font-medium text-white">Bias Normalization</h4>
                    <p className="text-sm text-gray-300">Team-specific bias values ensure fair comparison across different fanbases</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Confidence Levels</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <div className="flex items-center space-x-3">
                    <HiCheckCircle className="h-5 w-5 text-orange-400" />
                    <span className="font-medium text-white">High Confidence</span>
                  </div>
                  <span className="text-sm text-orange-400">80%+ samples</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <div className="flex items-center space-x-3">
                    <HiExclamationTriangle className="h-5 w-5 text-orange-400" />
                    <span className="font-medium text-white">Medium Confidence</span>
                  </div>
                  <span className="text-sm text-orange-400">30-79% samples</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <div className="flex items-center space-x-3">
                    <HiXCircle className="h-5 w-5 text-orange-400" />
                    <span className="font-medium text-white">Low Confidence</span>
                  </div>
                  <span className="text-sm text-orange-400">&lt;30% samples</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics and Metrics */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
          <HiChartBar className="h-6 w-6 mr-3 text-orange-600" />
          Statistics and Metrics
        </h2>
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-gray-800 rounded-xl border border-gray-700">
              <HiUsers className="h-8 w-8 text-orange-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Fan Distribution</h3>
              <p className="text-sm text-gray-300">
                Shows the breakdown of home fans and away fans in the analysis
              </p>
            </div>
            
            <div className="text-center p-6 bg-gray-800 rounded-xl border border-gray-700">
              <HiArrowTrendingUp className="h-8 w-8 text-orange-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Sentiment Trends</h3>
              <p className="text-sm text-gray-300">
                Real-time sentiment changes throughout the pre-match period
              </p>
            </div>
            
            <div className="text-center p-6 bg-gray-800 rounded-xl border border-gray-700">
              <HiCursorArrowRays className="h-8 w-8 text-orange-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Bias Analysis</h3>
              <p className="text-sm text-gray-300">
                Displays team-specific bias values and normalization effects
              </p>
            </div>
            
            <div className="text-center p-6 bg-gray-800 rounded-xl border border-gray-700">
              <HiBolt className="h-8 w-8 text-orange-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-white mb-2">Engagement Metrics</h3>
              <p className="text-sm text-gray-300">
                Comment volume, participation rates, and community activity levels
              </p>
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Key Performance Indicators</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-orange-400 mb-3">Data Quality Metrics</h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li>• <strong>Sample Size:</strong> Number of comments analyzed</li>
                  <li>• <strong>Data Freshness:</strong> How recent the data is</li>
                  <li>• <strong>Relevance Score:</strong> Weighted combination (30% quality_score + 30% engagement_score + 40% football_relevance) measuring comment value for analysis</li>
                  <li>• <strong>Engagement Score:</strong> Derived from Reddit upvotes/downvotes, indicating community validation of comment quality</li>
                  <li>• <strong>Football Relevance:</strong> Measures how relevant the comment is to football/match context</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium text-orange-400 mb-3">Confidence Indicators</h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li>• <strong>Overall Confidence:</strong> Prediction reliability score based on sample size and data quality</li>
                  <li>• <strong>Source Diversity:</strong> Only Reddit for now</li>
                  <li>• <strong>Bias Fairness:</strong> How balanced the analysis is after bias normalization</li>
                  <li>• <strong>Prediction Strength:</strong> Certainty of the outcome based on sentiment differences</li>
                  <li>• <strong>Effective Sample:</strong> Sum of relevance scores, indicating weighted comment contribution</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-700">
              <h4 className="font-medium text-orange-400 mb-2">Understanding Relevance Scoring</h4>
              <p className="text-sm text-gray-300 mb-2">
                Each comment receives a <strong>relevance_score</strong> (0.0-1.0) that determines its weight 
                in the analysis. This score combines three factors:
              </p>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• <strong>Quality Score (30%):</strong> Text quality metrics including length, formatting, and readability</li>
                <li>• <strong>Engagement Score (30%):</strong> Community validation through Reddit upvotes/downvotes</li>
                <li>• <strong>Football Relevance (40%):</strong> How relevant the comment is to football/match context</li>
              </ul>
              <p className="text-xs text-gray-400 mt-2">
                Comments with higher relevance scores contribute more to the final sentiment analysis, ensuring 
                that high-quality, football-relevant, community-validated comments have the most impact.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Accuracy and Limitations */}
      <div className="card">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
          <HiCursorArrowRays className="h-6 w-6 mr-3 text-orange-600" />
          Accuracy and Limitations
        </h2>
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">System Strengths</h3>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <HiCheckCircle className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Bias-Aware Analysis</h4>
                    <p className="text-sm text-gray-300">
                      Accounts for team-specific sentiment patterns for fair comparison
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <HiCheckCircle className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Real-time Processing</h4>
                    <p className="text-sm text-gray-300">
                      Live sentiment updates during pre-match period for immediate insights
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <HiCheckCircle className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Football-Specific AI</h4>
                    <p className="text-sm text-gray-300">
                      XLM-RoBERTa model enhanced with football terminology and fan language patterns for accurate sentiment analysis
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <HiCheckCircle className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Relevance Scoring</h4>
                    <p className="text-sm text-gray-300">
                      Weighted scoring system ensures high-quality, football-relevant comments have more impact on predictions
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-white">Limitations</h3>
              
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <HiExclamationTriangle className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Sample Size Dependency</h4>
                    <p className="text-sm text-gray-300">
                      Accuracy improves with more comments, limited by Reddit activity
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <HiExclamationTriangle className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Language Barriers</h4>
                    <p className="text-sm text-gray-300">
                      Primarily English-focused, may miss non-English fan sentiment
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <HiExclamationTriangle className="h-5 w-5 text-orange-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-white">Platform Dependency</h4>
                    <p className="text-sm text-gray-300">
                      Relies on Reddit data, may not represent all fan opinions
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
            <h3 className="text-lg font-semibold text-orange-400 mb-4">Important Disclaimers</h3>
            <ul className="space-y-2 text-sm text-gray-300">
              <li>• <strong>Predictions are for entertainment purposes only</strong> - not financial advice</li>
              <li>• <strong>Sentiment analysis is not 100% accurate</strong> - human language is complex and nuanced</li>
              <li>• <strong>Match outcomes depend on many factors</strong> - sentiment is just one indicator</li>
              <li>• <strong>Data quality varies</strong> - some matches may have limited fan engagement</li>
              <li>• <strong>Bias normalization is imperfect</strong> - some team-specific patterns may not be captured</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="card bg-gradient-to-r from-orange-900/30 to-orange-800/30 border-orange-500/30">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">
            Ready to Explore?
          </h2>
          <p className="text-lg text-gray-300 mb-6">
            Now that you understand how our system works, explore upcoming, finished or even live games 
            to see the sentiment analysis in action!
          </p>
          <Link 
            to="/" 
            className="inline-flex items-center px-6 py-3 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors"
          >
            <HiSignal className="h-5 w-5 mr-2" />
            View Games
          </Link>
        </div>
      </div>
    </div>
  )
}

export default HowItWorksPage
